import * as Yup from "yup"
import { Form, Formik, FormikHelpers } from "formik"
import { addCandidatesToViviers } from "../core/_requests"
import { toast } from "react-toastify"
import MyAlert from "@common-utils/MyAlert"
import { CONST_API_UTILS_GET_VIVIERS_ENDPOINT } from "../../../../endpoints"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import React from "react"
import MyRadiosWithLabelAndDescriptionFormField from "@common-components/fields/MyRadiosWithLabelAndDescriptionFormField"
import MySelectGetAllDataFromServerFormField from "@common-components/fields/MySelectGetAllDataFromServerFormField"
import MyInputFormField from "@common-components/fields/MyInputFormField"
import { ICandidatModel, ICandidatVivierModel } from "@common-models/*"
import MyCard from "@common-utils/MyCard"

const CandidatAddToViviersForm = ({ candidats, onSuccessCallBack, onSubmitCallBack, onErrorCallBack }: ICandidatAddToViviersFormModel) => {
     const schema = Yup.object().shape({
          vivierExistantOrNew: Yup.string().label(`"Existant ou nouveau"`).required(),
          viviers: Yup.array()
               .when("vivierExistantOrNew", {
                    is: "existant",
                    then: schema => schema.required().min(1),
               })
               .nullable()
               .label(`"Viviers"`),
          intituleNewVivier: Yup.string()
               .when("vivierExistantOrNew", {
                    is: "new",
                    then: schema => schema.required(),
               })
               .label(`"Titre"`),
     })

     const initialValues: { vivierExistantOrNew?: "new" | "existant"; viviers?: string[]; intituleNewVivier?: string } = {}

     function handleSubmit(
          values: { vivierExistantOrNew?: "new" | "existant"; viviers?: string[]; intituleNewVivier?: string },
          helpers: FormikHelpers<{ vivierExistantOrNew?: "new" | "existant"; viviers?: string[]; intituleNewVivier?: string }>
     ) {
          onSubmitCallBack()

          helpers.setStatus(null)
          addCandidatesToViviers(
               candidats.map(item => item.id!),
               values.vivierExistantOrNew === "new" ? (values.intituleNewVivier as string) : null,
               values.vivierExistantOrNew === "existant" ? (values.viviers?.map(vv => parseInt(vv)) as number[]) : null
          )
               .then(r => {
                    onSuccessCallBack(r.data)

                    // UI message
                    if (values.vivierExistantOrNew === "new") {
                         toast.success("Le candidat a bien été ajouté au nouveau vivier.")
                    } else {
                         toast.success(`Le candidat a bien été ajouté ${values.viviers!.length > 1 ? "aux viviers" : "au vivier"}.`)
                    }
               })
               .catch(() => {
                    onErrorCallBack()
                    helpers.setStatus("Erreur inattendue. veuillez réessayer.")
                    helpers.setSubmitting(false)
               })
     }

     return (
          <>
               <MyCard classNames={"mb-5 border"} defaultIsCollapsed={true}>
                    <MyCard.Header isCollapseIconShown>Candidats concernés ( {candidats.length} )</MyCard.Header>
                    <MyCard.Body>
                         <>
                              {candidats.map((item, index) => (
                                   <div key={index}>{item.prenomNom}</div>
                              ))}
                         </>
                    </MyCard.Body>
               </MyCard>
               <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
                    {helpers => {
                         return (
                              <Form noValidate autoComplete="off">
                                   {helpers.status && (
                                        <MyAlert type={"danger"} classNames={"mb-4"}>
                                             {helpers.status}
                                        </MyAlert>
                                   )}

                                   <div className="d-grid gap-4">
                                        {/* Field vivierExistantOrNew */}
                                        <MyRadiosWithLabelAndDescriptionFormField
                                             name={"vivierExistantOrNew"}
                                             formikProps={helpers}
                                             label={"Choisissez une option"}
                                             validationSchema={schema}
                                             radios={[
                                                  {
                                                       label: "Vivier existant",
                                                       description: "Ajouter à un vivier existant",
                                                       faIcon: "user",
                                                       iconColor: "primary",
                                                       iconWrapperColor: "light-primary",
                                                       radioValue: "existant",
                                                  },
                                                  {
                                                       label: "Nouveau vivier",
                                                       description: "Ajouter à un nouveau vivier",
                                                       faIcon: "plus",
                                                       iconColor: "success",
                                                       iconWrapperColor: "light-success",
                                                       radioValue: "new",
                                                  },
                                             ]}
                                             classNameLabel={"col-lg-4"}
                                        />

                                        {/* Field viviers */}
                                        {helpers.values.vivierExistantOrNew === "existant" && (
                                             <MySelectGetAllDataFromServerFormField
                                                  label={<span className={"required"}>Choisissez un vivier</span>}
                                                  name={"viviers"}
                                                  formikProps={helpers}
                                                  validationSchema={schema}
                                                  endpoint={CONST_API_UTILS_GET_VIVIERS_ENDPOINT}
                                                  placeholder={"Choisissez un vivier"}
                                                  method={"GET"}
                                                  payload={{
                                                       includeMyOwnViviers: true,
                                                       includeTeamViviers: true,
                                                  }}
                                                  classNameLabel={"col-lg-4"}
                                                  classNameField={"col-lg-8"}
                                                  isMulti
                                             />
                                        )}

                                        {/* Field intituleNewVivier */}
                                        {helpers.values.vivierExistantOrNew === "new" && (
                                             <MyInputFormField
                                                  label={<span className={"required"}>Nouveau vivier</span>}
                                                  name={"intituleNewVivier"}
                                                  formikProps={helpers}
                                                  validationSchema={schema}
                                                  placeholder={"Saisissez un nouveau vivier"}
                                                  classNameLabel={"col-lg-4"}
                                                  classNameField={"col-lg-8"}
                                             />
                                        )}
                                   </div>

                                   <div className="separator my-4" />

                                   {/* Submit button */}
                                   <div className={"text-end"}>
                                        <button className={"btn btn-sm btn-primary d-inline-flex align-items-center"} type={"submit"} disabled={helpers.isSubmitting}>
                                             {!helpers.isSubmitting ? "Ajouter" : <MySimpleSpinner size={"sm"} />}
                                        </button>
                                   </div>
                              </Form>
                         )
                    }}
               </Formik>{" "}
          </>
     )
}

export interface ICandidatAddToViviersFormModel {
     candidats: ICandidatModel[]
     onSubmitCallBack: () => void
     onSuccessCallBack: (items: ICandidatVivierModel[]) => void
     onErrorCallBack: () => void
}

export default CandidatAddToViviersForm
